import { TransformFn } from "objectro/lib/types";
import { concat, mergeTo } from "../shared/transforms";
import {
  get,
  stripNullProperties,
  getISO8601DateStr
} from "../../utils/helpers";
import { getIso3 } from "../../utils/countries";
import { POJO } from "../../types";
import { CustomerFormStateObject } from "../../hooks/useCustomerFormState";

const formatZipCode = (input?: any) => {
  const country = get(input, "addressCountry", null);
  const zipCode = get(input, "postalCode", null);
  if (country === "US" || country === "VI") {
    if (zipCode !== null && zipCode.includes("-")) {
      const formattedZipCode = zipCode.split("-")[0];
      return formattedZipCode;
    }
    return zipCode;
  } else {
    return zipCode;
  }
};

/**
 * The internal address is always on the input object.
 *
 * We pluck the values from the input object, as targetting a single
 * property (for the moment) won't be useful (yet).
 */
export const transformInternalAddressToPostalAddress: TransformFn = (
  value: Partial<CustomerFormStateObject>,
  propName?: string,
  input?: any
) => {
  let region = get(input, "addressRegion", null);
  if (region && region.indexOf("_") >= 0) {
    region = region.split("_")[1];
  }

  const _postalAddress: POJO | null = stripNullProperties({
    fullStreetAddress:
      [
        get(input, "streetAddress1", null),
        get(input, "streetAddress2", null),
        get(input, "streetAddress3", null)
      ]
        .filter(x => x)
        .join(",") || null,
    streetAddress1: get(input, "streetAddress1", null),
    streetAddress2: get(input, "streetAddress2", null),
    apartNumber: get(input, "apartNumber", null),
    city: get(input, "addressLocality", null),
    region: region,
    postalCode: formatZipCode(input),
    country: get(input, "addressCountry", null)
  });

  return _postalAddress;
};

// Map the internal data model to the DDC Backend Customer object
// customerFormState -> DDCCustomer
export const toDDCCustomer = {
  gender: "gender",
  identifier: "identifier",
  honorificPrefix: "title",
  givenName: "firstName",
  familyName: "lastName",
  alternateGivenName1: "alternateFirstName1",
  alternateGivenName2: "alternateFirstName2",
  alternateFamilyName1: "alternateLastName1",
  alternateFamilyName2: "alternateLastName2",
  alternateFullName1: (value: any, propName?: string, input?: any) => {
    return {
      alternateFullName1: concat(
        input,
        "alternateGivenName1",
        "alternateFamilyName1"
      )
    };
  },
  alternateFullName2: (value: any, propName?: string, input?: any) => {
    return {
      alternateFullName2: concat(
        input,
        "alternateGivenName2",
        "alternateFamilyName2"
      )
    };
  },
  // In case the date is not a string, we convert to a YYYY-MM-DD string
  birthDate: (value: Date | number | string | null | undefined) => {
    return {
      birthDate: value ? new Date(value).toISOString() : null
    };
  },
  birthDateYear: (value: number | string | null | undefined) => {
    return {
      birthDateYear: value === "0000" ? null : value
    };
  },
  birthDateMonth: "birthDateMonth",
  birthDateDay: "birthDateDay",
  nationality: (value: string | null | undefined) => {
    return {
      nationality: value && getIso3(value)
    };
  },
  eReceipt: "eReceipt",
  email: "email",
  telephone: "homePhoneNumber",
  mobileTelephone: "mobilePhoneNumber",
  mobileTelephoneDqe: "mobilePhoneNumber",
  wechatId: "wechatId",
  lineId: "lineId",
  // This will map the shallow InternalAddress properties to a
  // nested object within the DDCCustomer object.
  address: mergeTo("address", transformInternalAddressToPostalAddress),
  appsConsent: "appsConsent",
  emailConsent: "emailConsent",
  postConsent: "postConsent",
  phoneConsent: "phoneConsent",
  salesPerson: "advisorCode",
  creationStore: "storeCode",
  storeCountry: "storeCountry",
  storeName: "storeName",
  storeLocation: "storeLocation",
  preferredLanguage: "preferredLanguage",
  signatureData: "signatureData",
  notes: "notes",
  isAddressValid: "isAddressValid",
  isEmailValid: "isEmailValid",
  isMobilePhoneValid: "isMobilePhoneValid",
  FiscalId: "FiscalId",
  PassportNumber: "PassportNumber",
  katakanaFirstname: (value: any, propName?: string, input?: any) => {
    if (input.preferredLanguage === "ja") {
      return {
        AlternateFirstName: get(input, "katakanaFirstname")
      };
    }
  },
  katakanaLastname: (value: any, propName?: string, input?: any) => {
    if (input.preferredLanguage === "ja") {
      return {
        AlternateLastName: get(input, "katakanaLastname")
      };
    }
  },
  // customFields: may be defined in config and handled in api.ts
  creationDate: () => {
    return {
      creationDate: getISO8601DateStr()
    };
  }
};
